import { useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { useProvideSnackBar } from './useSnackBar';
import { WorkerDevice } from '../@types/employeeData';

const FIREBASE_API_ENDPOINT = (window as any).FIREBASE_API_URL;
const STATUS_CHECK_INTERVAL = 2000;

export type BatchStatus = {
    processed: number;
    total: number;
    success: number;
    failure: number;
    progress: number;
    completed: boolean;
};

type UseSendNotificationParams = {
    onSuccess?: () => void;
    onStatusUpdate?: (status: BatchStatus) => void;
};

export const useSendNotification = ({ onSuccess, onStatusUpdate }: UseSendNotificationParams = {}) => {
    const { user } = useAuth();
    const { showSuccess, showError, showResponseError, showInfo } = useProvideSnackBar();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const checkBatchStatus = async (batchId: string): Promise<BatchStatus | null> => {
        try {
            const response = await fetch(`${FIREBASE_API_ENDPOINT}/v2/notification/status?batchId=${batchId}`, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user?.accessToken}`,
                },
            });

            if (!response.ok) {
                return null;
            }

            return await response.json();
        } catch (error) {
            console.error('Error checking batch status:', error);
            return null;
        }
    };

    const startStatusPolling = (batchId: string) => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        setIsLoading(true);

        checkBatchStatus(batchId).then(status => {
            if (status) {
                onStatusUpdate?.(status);
                if (status.completed) {
                    setIsLoading(false);
                    if (status.failure === 0) {
                        showSuccess(`Értesítés sikeresen elküldve. (${status.success}/${status.total})`);
                    } else {
                        showError(`Értesítés elküldve részben sikeresen. Sikeres: ${status.success}, Sikertelen: ${status.failure} (${status.total} összesen)`);
                    }
                    onSuccess?.();
                    return;
                }
            }

            intervalRef.current = setInterval(async () => {
                const status = await checkBatchStatus(batchId);
                if (status) {
                    onStatusUpdate?.(status);
                    if (!status.completed) {
                        showInfo('Értesítések küldése folyamatban... (' + status.progress.toFixed(0) + '%)');
                    }

                    if (status.completed) {
                        if (intervalRef.current) {
                            clearInterval(intervalRef.current);
                            intervalRef.current = null;
                        }
                        setIsLoading(false);

                        if (status.failure === 0) {
                            showSuccess(`Értesítés sikeresen elküldve. (${status.success}/${status.total})`);
                        } else {
                            showError(
                                `Értesítés elküldve részben sikeresen. Sikeres: ${status.success}, Sikertelen: ${status.failure} (${status.total} összesen)`
                            );
                        }
                        onSuccess?.();
                    }
                }
            }, STATUS_CHECK_INTERVAL);
        });
    };

    const sendNotification = async (workerDevices: WorkerDevice[], title: string, body: string, senderName?: string) => {
        try {
            setIsLoading(true);
            const response = await fetch(`${FIREBASE_API_ENDPOINT}/v2/notification/`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user?.accessToken}`,
                },
                body: JSON.stringify({
                    senderName: senderName || user?.name || '',
                    title: title,
                    body: body,
                    workerDevices: workerDevices,
                    isPersonal: true,
                }),
            });

            if (!response.ok) {
                setIsLoading(false);
                await showResponseError(response);
                return false;
            }
            const responseData = await response.json();

            startStatusPolling(responseData.batchId);
            showSuccess('Értesítés küldése elindítva. Folyamatban...');

            return true;
        } catch (error) {
            setIsLoading(false);
            showError('Értesítés küldése sikertelen.');
            return false;
        }
    };

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    return { sendNotification, isLoading };
};
